import React from "react"
import { sessionApiClient } from "@planningcenter/cc-api-client"

import {
  useLocalStorage,
  removeLocalStorageValue,
} from "source/shared/hooks/useLocalStorage"

const NOTIFICATIONS_COUNT_STORAGE_KEY = "header:notificationCount"
const CHAT_PERSON_STORAGE_KEY = "header:chatPerson"

export { Header } from "./Header"

export function resetCache() {
  removeLocalStorageValue(NOTIFICATIONS_COUNT_STORAGE_KEY)
  removeLocalStorageValue(CHAT_PERSON_STORAGE_KEY)
}

export function useNotificationCount({
  personIdentified,
  notificationsChannel,
  notificationDelay = 1000,
}) {
  const [version, setVersion] = React.useState(0)
  const [notificationCount, setNotificationCount] = useLocalStorage(
    NOTIFICATIONS_COUNT_STORAGE_KEY,
    0,
  )

  // Fetch the current unread count from the server
  React.useEffect(() => {
    if (!personIdentified) return

    let fresh = true
    let timeout = window.setTimeout(() => {
      sessionApiClient
        .get(`/notifications/v2/me/notifications?where[read_at]=&per_page=0`)
        .then(
          (payload) => fresh && setNotificationCount(payload.meta.total_count),
        )
        .catch(() => setNotificationCount(null))
    }, notificationDelay)

    return () => {
      fresh = false
      window.clearTimeout(timeout)
    }
  }, [personIdentified, version, notificationDelay])

  const incrementVersion = React.useCallback(
    () => setVersion((i) => i + 1),
    [setVersion],
  )

  // Listen for updates to trigger a fetch of the unread count from the server
  React.useEffect(() => {
    if (!notificationsChannel || !personIdentified) return

    const events = [
      `notifications.church_center.v2.events.notification.updated`,
      `notifications.church_center.v2.events.notification.created`,
      `notifications.church_center.v2.events.notification.destroyed`,
    ]
    const unbindFunctions = events.map((event) =>
      notificationsChannel.bind(event, incrementVersion),
    )
    return () => unbindFunctions.forEach((unbind) => unbind())
  }, [notificationsChannel, personIdentified])

  return notificationCount
}

export function useChatHeaderProps({
  channel,
  personId,
  personIdentified,
  delay = 1000,
}) {
  const [version, setVersion] = React.useState(0)
  const [chatPersonData, setChatPersonData] = useLocalStorage(
    CHAT_PERSON_STORAGE_KEY,
    { can_chat_pending_age_check: false, unread_messages: false },
  )

  React.useEffect(() => {
    if (!personIdentified) return

    let fresh = true
    let timeout = window.setTimeout(() => {
      sessionApiClient
        .get(
          `/chat/v2/me?fields[Person]=can_chat_pending_age_check,unread_messages`,
        )
        .then(
          (payload) =>
            fresh &&
            setChatPersonData({
              can_chat_pending_age_check:
                payload.data.attributes.can_chat_pending_age_check,
              unread_messages: payload.data.attributes.unread_messages,
            }),
        )
        .catch(() =>
          setChatPersonData({
            can_chat_pending_age_check: false,
            unread_messages: false,
          }),
        )
    }, delay)

    return () => {
      fresh = false
      window.clearTimeout(timeout)
    }
  }, [personIdentified, version, delay])

  const incrementVersion = React.useCallback(
    () => setVersion((i) => i + 1),
    [setVersion],
  )

  React.useEffect(() => {
    if (!channel || !personIdentified || !personId) return

    const event = "STREAM_USER_UPDATED"
    const unbind = channel.bind(event, incrementVersion)
    return () => unbind()
  }, [channel, personIdentified, personId])

  return {
    chatUnread: chatPersonData.unread_messages,
    pcoChatEnabled: chatPersonData.can_chat_pending_age_check,
  }
}
